/**
 * "Prozentsatz der Miete" management-fee method on the property page (EHV-1093, D3).
 *
 * Impower only knows FLAT / PER_UNIT (`feeType` / `administrationFees`), so this
 * extends the native fee-type radio group (Pauschale / Pro Einheit) with a third
 * option plus a rent-base select and a percentage input. The settings live in Lux
 * (`PropertyManagementFeeSettings`) and are loaded/saved through
 * `/api/impowerX/properties/{propertyId}/management-fee-settings` using the
 * NextAuth session cookie (`credentials: 'include'`, per repo rule) — they drive
 * the prefill of the management invoice modal (D2).
 *
 * The native radios stay fully functional (Impower keeps its own FLAT/PER_UNIT
 * state); while the percentage method is active their check mark is only hidden
 * visually. Saving happens together with the property via the header save
 * buttons; Abbrechen restores the last saved values.
 *
 * Called from onPropertyPageMatch.js. Gulp concatenates src/**\/*.js into one
 * IIFE (features/ before pageInjects/), and this top-level function declaration
 * is hoisted, so the call site never sees it undefined.
 */
function initManagementFeePercentageInject({
  propertyId,
  baseApiUrl,
  shouldDisableInjectedFields,
}) {
  if (!propertyId) return null

  const RENT_BASE_OPTIONS = [
    { value: 'net_cold', label: 'Nettokaltmiete' },
    { value: 'net_cold_plus_vat', label: 'Nettokaltmiete zzgl. USt.' },
    { value: 'gross', label: 'Bruttomiete' },
  ]
  const RENT_BASE_VALUES = RENT_BASE_OPTIONS.map((option) => option.value)
  const SETTINGS_URL = `${baseApiUrl}/api/impowerX/properties/${propertyId}/management-fee-settings`
  const RADIO_CLASS = 'lux-fee-pct-radio'
  const DETAILS_CLASS = 'lux-fee-pct-details'
  const GROUP_ACTIVE_CLASS = 'lux-fee-pct-active'
  const STYLE_ID = 'lux-fee-pct-styles'
  const SAVE_BUTTON_LABELS = [
    'Speichern',
    'Save',
    'Speichern und Weiter',
    'Save and Continue',
    'Save all',
    'Als Entwurf Speichern',
    'Bereit zur Verbuchung',
    'Ready for invoice',
  ]
  const CANCEL_BUTTON_LABELS = ['Abbrechen', 'Cancel']

  /** Baseline the UI edits against and Abbrechen restores to. Matches the
   * stored Lux row except while `serverOutOfSync` is set. */
  let savedSettings = null
  let state = null
  let isSaving = false
  let disposed = false
  let syncTimer = null
  /**
   * Bumped on Abbrechen so an in-flight PUT cannot apply after the UI (and
   * Impower's property form) has already been restored.
   */
  let saveEpoch = 0
  /** A Speichern that arrived while a PUT was in flight, run afterwards. */
  let saveQueued = false
  /**
   * The rollback after a cancelled save failed, so Lux still holds the
   * cancelled edit while `savedSettings` holds what the user wants. Forces
   * `isDirty()` so the next Speichern writes `state` (and thereby the restore)
   * even when the fields look unchanged. Cleared once a PUT succeeds.
   */
  let serverOutOfSync = false

  const settingsFromResponse = (settings) => ({
    usePercentageOfRent: Boolean(settings.usePercentageOfRent),
    rentBase: RENT_BASE_VALUES.includes(settings.rentBase)
      ? settings.rentBase
      : 'net_cold',
    percentage:
      typeof settings.percentage === 'number' ? settings.percentage : null,
  })

  const putSettings = async (payload) => {
    const response = await fetch(SETTINGS_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        usePercentageOfRent: payload.usePercentageOfRent,
        rentBase: payload.rentBase,
        percentage:
          payload.percentage > 0 && payload.percentage <= 100
            ? payload.percentage
            : null,
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data?.settings) {
      throw new Error(data?.error || `HTTP ${response.status}`)
    }
    return settingsFromResponse(data.settings)
  }

  const parsePercentage = (raw) => {
    const text = String(raw ?? '').trim()
    if (!text) return null
    // Tolerant like the D2 modal: "1.234,56" and "1234.56" both parse.
    const normalised = text.replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.')
    const parsed = Number.parseFloat(normalised)
    return Number.isFinite(parsed) ? parsed : null
  }

  const formatPercentage = (value) => {
    if (value == null) return ''
    return formatGermanNumber(String(value).replace('.', ','), true, true)
  }

  const isDisabled = () =>
    typeof shouldDisableInjectedFields === 'function'
      ? shouldDisableInjectedFields()
      : false

  const settingsEqual = (a, b) =>
    a.usePercentageOfRent === b.usePercentageOfRent &&
    a.rentBase === b.rentBase &&
    a.percentage === b.percentage

  const isDirty = () => {
    if (!savedSettings || !state) return false
    return serverOutOfSync || !settingsEqual(savedSettings, state)
  }

  const injectStyles = () => {
    if (document.getElementById(STYLE_ID)) return
    const style = document.createElement('style')
    style.id = STYLE_ID
    // While the percentage method is active, hide the check mark of the native
    // FLAT/PER_UNIT radios without touching Impower's React state.
    style.textContent = `
      .${GROUP_ACTIVE_CLASS} .ant-radio-wrapper:not(.${RADIO_CLASS}) .ant-radio-inner {
        border-color: #d9d9d9 !important;
      }
      .${GROUP_ACTIVE_CLASS} .ant-radio-wrapper:not(.${RADIO_CLASS}) .ant-radio-inner::after {
        opacity: 0 !important;
        transform: scale(0) !important;
      }
      .${DETAILS_CLASS} { display: flex; flex-wrap: wrap; gap: 24px; margin-top: 12px; margin-bottom: 4px; }
      .${DETAILS_CLASS} .lux-fee-pct-field { width: 280px; }
      .${DETAILS_CLASS} .lux-fee-pct-field-pct { width: 160px; }
      .${DETAILS_CLASS} .ant-form-item { margin-bottom: 0; }
      .${DETAILS_CLASS} .ant-form-item-control-input-content { position: relative; }
    `
    document.head.appendChild(style)
  }

  /**
   * The native fee-type radio group: the ant radio group on the property form
   * whose options cover both the flat and the per-unit method. Found via label
   * text (German or English UI) because Impower exposes no stable hook.
   */
  const coversBothFeeMethods = (labels) => {
    const hasFlat = labels.some(
      (text) =>
        text.includes('pauschal') ||
        text.includes('fixum') ||
        text.includes('flat')
    )
    const hasPerUnit = labels.some(
      (text) => text.includes('einheit') || text.includes('unit')
    )
    return hasFlat && hasPerUnit
  }

  const findFeeRadioGroup = () => {
    // Preferred: wrappers as direct children of a common container (standard
    // ant-radio-group markup).
    const wrappers = document.querySelectorAll(
      '.form-content .ant-radio-wrapper'
    )
    const seenParents = new Set()
    for (const wrapper of wrappers) {
      const parent = wrapper.parentElement
      if (!parent || seenParents.has(parent)) continue
      seenParents.add(parent)

      const labels = Array.from(
        parent.querySelectorAll(':scope > .ant-radio-wrapper')
      ).map((el) => (el.textContent || '').toLowerCase())
      if (coversBothFeeMethods(labels)) return parent
    }

    // Fallback: wrappers nested deeper inside an explicit ant-radio-group.
    for (const group of document.querySelectorAll(
      '.form-content .ant-radio-group'
    )) {
      const labels = Array.from(
        group.querySelectorAll('.ant-radio-wrapper')
      ).map((el) => (el.textContent || '').toLowerCase())
      if (coversBothFeeMethods(labels)) return group
    }
    return null
  }

  const buildRadio = () => {
    const label = document.createElement('label')
    label.className = `ant-radio-wrapper ${RADIO_CLASS}`
    label.innerHTML = `
      <span class="ant-radio">
        <input type="radio" class="ant-radio-input" name="lux-fee-pct-method" value="PERCENTAGE_OF_RENT" />
        <span class="ant-radio-inner"></span>
      </span>
      <span>Prozentsatz der Miete</span>
    `
    label.addEventListener('click', (event) => {
      event.preventDefault()
      if (isDisabled() || !state || state.usePercentageOfRent) return
      state.usePercentageOfRent = true
      syncUi()
    })
    return label
  }

  const formItemHtml = (label, controlHtml, extraFieldClass = '') => `
    <div class="lux-fee-pct-field ${extraFieldClass}">
      <div class="ant-row ant-form-item" style="row-gap: 0px;">
        <div class="ant-col ant-form-item-label">
          <label><span>${label}</span></label>
        </div>
        <div class="ant-col ant-form-item-control">
          <div class="ant-form-item-control-input">
            <div class="ant-form-item-control-input-content">
              ${controlHtml}
            </div>
          </div>
        </div>
      </div>
    </div>
  `

  const closeRentBaseDropdown = (details) => {
    details
      ?.querySelector('.lux-fee-pct-dropdown')
      ?.classList.add('ant-select-dropdown-hidden')
    details
      ?.querySelector('.lux-fee-pct-select')
      ?.classList.remove('ant-select-open')
  }

  const stepPercentage = (input, direction) => {
    if (!state || isDisabled()) return
    const current = parsePercentage(input.value) || 0
    const next = direction === 'up' ? current + 1 : current - 1
    state.percentage = Number.isFinite(next) ? next : current
    input.value = formatPercentage(state.percentage)
  }

  const buildDetails = () => {
    // Same ant-select / ant-input-number markup as generateInputField, without
    // impowerx-* classes so the property-page save does not collect these as
    // ImpowerX fields.
    const details = document.createElement('div')
    details.className = DETAILS_CLASS
    details.style.display = 'none'
    details.innerHTML =
      formItemHtml(
        'Mietbasis',
        `
        <div class="ant-select ant-select-in-form-item Select ant-select-single ant-select-show-arrow lux-fee-pct-select" style="position: relative; overflow: visible;">
          <div class="ant-select-selector">
            <span class="ant-select-selection-item" title=""></span>
          </div>
          <span class="ant-select-arrow" unselectable="on" aria-hidden="true" style="user-select: none;">
            <span role="img" class="anticon">
              <svg width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M0 0L5 5L10 0H0Z" fill="#000000"></path>
              </svg>
            </span>
          </span>
        </div>
        <div class="dropdown-wrapper lux-fee-pct-dropdown-wrap" style="position: absolute; top: 0; left: 0; width: 100%;">
          <div>
            <div class="ant-select-dropdown ant-select-dropdown-placement-bottomRight ant-select-dropdown-hidden lux-fee-pct-dropdown" style="z-index: 2000; width: 100%; left: 0; top: 45px;">
              <div class="rc-virtual-list" style="position: relative;">
                <div class="rc-virtual-list-holder" style="max-height: 256px; overflow-y: auto;">
                  <div class="rc-virtual-list-holder-inner" style="display: flex; flex-direction: column;">
                    ${RENT_BASE_OPTIONS.map(
                      (option) => `
                      <div class="ant-select-item ant-select-item-option lux-fee-pct-option" data-value="${option.value}" title="${option.label}">
                        <div class="ant-select-item-option-content">
                          <div class="select-menu-item">${option.label}</div>
                        </div>
                      </div>`
                    ).join('')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `
      ) +
      formItemHtml(
        'Prozentsatz',
        `
        <div class="ant-input-number InputNumber Input lux-fee-pct-number">
          <div class="ant-input-number-handler-wrap lux-fee-pct-handlers">
            <span unselectable="on" role="button" aria-label="Increase Value" class="ant-input-number-handler ant-input-number-handler-up">
              <span role="img" class="anticon">
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 0H3V3H0V5H3V8H5V5H8V3H5V0Z" fill="black"></path>
                </svg>
              </span>
            </span>
            <span unselectable="on" role="button" aria-label="Decrease Value" class="ant-input-number-handler ant-input-number-handler-down">
              <span role="img" class="anticon">
                <svg width="8" height="2" viewBox="0 0 8 2" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect x="8" width="2" height="8" transform="rotate(90 8 0)" fill="black"></rect>
                </svg>
              </span>
            </span>
          </div>
          <div class="ant-input-number-input-wrap">
            <input autocomplete="off" class="ant-input-number-input lux-fee-pct-percentage" type="text" inputmode="decimal" placeholder="0,00" style="text-align: right;" />
            <span class="suffix suffix-with-controls lux-fee-pct-suffix">%</span>
          </div>
        </div>
      `,
        'lux-fee-pct-field-pct'
      )

    const selectRoot = details.querySelector('.lux-fee-pct-select')
    const dropdown = details.querySelector('.lux-fee-pct-dropdown')
    const percentageInput = details.querySelector('.lux-fee-pct-percentage')

    selectRoot.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      if (isDisabled() || !state) return
      dropdown.classList.toggle('ant-select-dropdown-hidden')
      selectRoot.classList.toggle(
        'ant-select-open',
        !dropdown.classList.contains('ant-select-dropdown-hidden')
      )
    })
    dropdown.addEventListener('click', (event) => {
      const option = event.target.closest('.lux-fee-pct-option')
      if (!option || !state) return
      event.preventDefault()
      event.stopPropagation()
      const value = option.getAttribute('data-value')
      if (RENT_BASE_VALUES.includes(value)) {
        state.rentBase = value
        syncUi()
      }
      closeRentBaseDropdown(details)
    })
    percentageInput.addEventListener('input', (event) => {
      if (!state) return
      state.percentage = parsePercentage(event.target.value)
    })
    percentageInput.addEventListener('blur', (event) => {
      if (!state) return
      const display = formatPercentage(state.percentage)
      if (event.target.value !== display) event.target.value = display
    })
    details.addEventListener(
      'click',
      (event) => {
        const up = event.target.closest('.ant-input-number-handler-up')
        const down = event.target.closest('.ant-input-number-handler-down')
        if (!up && !down) return
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        stepPercentage(percentageInput, up ? 'up' : 'down')
      },
      true
    )
    percentageInput.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        event.stopImmediatePropagation()
        stepPercentage(percentageInput, 'up')
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        event.stopImmediatePropagation()
        stepPercentage(percentageInput, 'down')
      }
    })
    return details
  }

  /**
   * (Re-)inserts the radio and the details row. Impower re-renders the section
   * content freely (open/close, edit mode), which drops injected nodes — the
   * MutationObserver below calls this again whenever that happens.
   */
  const ensureInjected = () => {
    if (disposed || !state) return
    const group = findFeeRadioGroup()
    if (!group) return

    injectStyles()

    if (!group.querySelector(`.${RADIO_CLASS}`)) {
      group.appendChild(buildRadio())
    }

    let details = group.parentElement.querySelector(`:scope > .${DETAILS_CLASS}`)
    if (!details) {
      details = buildDetails()
      group.insertAdjacentElement('afterend', details)
    }

    // A native radio click switches back to the Impower-managed method.
    if (!group.dataset.luxFeePctBound) {
      group.dataset.luxFeePctBound = '1'
      group.addEventListener('click', (event) => {
        const wrapper = event.target.closest('.ant-radio-wrapper')
        if (!wrapper || wrapper.classList.contains(RADIO_CLASS)) return
        if (isDisabled() || !state || !state.usePercentageOfRent) return
        state.usePercentageOfRent = false
        syncUi()
      })
    }
  }

  /** Applies state + edit mode to the injected nodes (idempotent, diff-only). */
  const syncUi = () => {
    if (disposed || !state) return
    const group = findFeeRadioGroup()
    if (!group) return
    const radioWrapper = group.querySelector(`.${RADIO_CLASS}`)
    const details = group.parentElement.querySelector(
      `:scope > .${DETAILS_CLASS}`
    )
    if (!radioWrapper || !details) return

    const disabled = isDisabled()
    const active = state.usePercentageOfRent

    group.classList.toggle(GROUP_ACTIVE_CLASS, active)

    const radio = radioWrapper.querySelector('.ant-radio')
    const radioInput = radioWrapper.querySelector('.ant-radio-input')
    radioWrapper.classList.toggle('ant-radio-wrapper-checked', active)
    radioWrapper.classList.toggle('ant-radio-wrapper-disabled', disabled)
    radio.classList.toggle('ant-radio-checked', active)
    radio.classList.toggle('ant-radio-disabled', disabled)
    if (radioInput.checked !== active) radioInput.checked = active
    if (radioInput.disabled !== disabled) radioInput.disabled = disabled

    details.style.display = active ? 'flex' : 'none'
    if (!active || disabled) closeRentBaseDropdown(details)

    const rentBaseSelect = details.querySelector('.lux-fee-pct-select')
    rentBaseSelect.classList.toggle('ant-select-disabled', disabled)
    rentBaseSelect.classList.toggle('read-only', disabled)
    const selectedRentBase = RENT_BASE_OPTIONS.find(
      (option) => option.value === state.rentBase
    )
    const selectionItem = rentBaseSelect.querySelector(
      '.ant-select-selection-item'
    )
    const rentBaseLabel = selectedRentBase ? selectedRentBase.label : ''
    if (selectionItem.textContent !== rentBaseLabel) {
      selectionItem.textContent = rentBaseLabel
      selectionItem.setAttribute('title', rentBaseLabel)
    }
    details.querySelectorAll('.lux-fee-pct-option').forEach((option) => {
      option.classList.toggle(
        'ant-select-item-option-selected',
        option.getAttribute('data-value') === state.rentBase
      )
    })

    const numberWrap = details.querySelector('.lux-fee-pct-number')
    const percentageInput = details.querySelector('.lux-fee-pct-percentage')
    const suffix = details.querySelector('.lux-fee-pct-suffix')
    const handlerWrap = details.querySelector('.lux-fee-pct-handlers')
    numberWrap.classList.toggle('ant-input-number-disabled', disabled)
    numberWrap.style.borderColor = disabled ? 'transparent' : ''
    if (percentageInput.disabled !== disabled) {
      percentageInput.disabled = disabled
    }
    percentageInput.classList.toggle('ant-input-disabled', disabled)
    percentageInput.style.color = disabled ? 'rgba(0, 0, 0, 0.25)' : ''
    percentageInput.style.paddingRight = disabled ? '11px' : '24px'
    suffix.classList.toggle('suffix-with-controls', !disabled)
    suffix.style.color = disabled ? 'rgba(0, 0, 0, 0.25)' : ''
    handlerWrap.setAttribute(
      'style',
      disabled ? 'display: none !important; width: 0; overflow: hidden;' : ''
    )
    if (document.activeElement !== percentageInput) {
      const display = formatPercentage(state.percentage)
      if (percentageInput.value !== display) percentageInput.value = display
    }
  }

  const loadSettings = async () => {
    try {
      const response = await fetch(SETTINGS_URL, {
        headers: { Accept: 'application/json' },
        credentials: 'include',
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      const settings = data?.settings
      if (!settings) throw new Error('Malformed settings response')

      savedSettings = settingsFromResponse(settings)
      state = { ...savedSettings }
      ensureInjected()
      syncUi()
    } catch (error) {
      // Without loaded settings we never inject — a save from a default state
      // could silently overwrite an existing configuration.
      console.error(
        '[ImpowerX] Verwaltungsgebühr-Einstellungen konnten nicht geladen werden:',
        error
      )
    }
  }

  /**
   * Abbrechen won the race against a PUT that already stored the cancelled
   * edit: write the pre-save snapshot back so Lux matches the reverted Impower
   * form. Runs even after teardown — the stored row would otherwise keep
   * feeding the D2 prefill. `state` is left alone: the user may have edited
   * again after cancelling.
   *
   * On failure `savedSettings` keeps the pre-save snapshot (so Abbrechen and
   * the invalid-percentage fallback still restore the right values) and
   * `serverOutOfSync` records that Lux disagrees, which keeps `isDirty()` true
   * until a later Speichern lands the restore.
   */
  const rollbackCancelledSave = async (revertTo) => {
    try {
      savedSettings = await putSettings(revertTo)
      serverOutOfSync = false
      syncUi()
    } catch (error) {
      serverOutOfSync = true
      console.error(
        '[ImpowerX] Verwaltungsgebühr-Einstellungen konnten nach Abbrechen nicht zurückgesetzt werden:',
        error
      )
      if (!disposed) {
        showToast(
          'Die Verwaltungsgebühr-Einstellungen konnten nach Abbrechen nicht zurückgesetzt werden.',
          'error'
        )
      }
    }
  }

  const flushSave = async () => {
    if (disposed || !state || !savedSettings) return
    if (isSaving) {
      // Impower saves the rest of the property regardless, so a click during
      // an in-flight PUT (or its rollback) must not be dropped.
      saveQueued = true
      return
    }
    if (!isDirty()) return

    if (
      state.usePercentageOfRent &&
      !(state.percentage > 0 && state.percentage <= 100)
    ) {
      // Impower's property save cannot be vetoed from here, so keeping the
      // rejected input on screen would show a method that is not stored and
      // would leak into the D2 prefill. Fall back to the saved values instead.
      restoreSavedState()
      showToast(
        'Bitte einen Prozentsatz zwischen 0 und 100 angeben – die zuletzt gespeicherte Verwaltungsgebühr-Einstellung bleibt bestehen.',
        'warning'
      )
      return
    }

    const epoch = saveEpoch
    const revertTo = { ...savedSettings }
    const pending = { ...state }
    isSaving = true
    try {
      const saved = await putSettings(pending)
      if (epoch !== saveEpoch) {
        await rollbackCancelledSave(revertTo)
        return
      }
      if (disposed) return
      savedSettings = saved
      serverOutOfSync = false
      // Adopt the server-normalised values only if the user did not edit while
      // the PUT was in flight — otherwise those edits (typically followed by a
      // queued Speichern) would be wiped by the earlier snapshot.
      if (settingsEqual(state, pending)) {
        state = { ...savedSettings }
      }
      syncUi()
    } catch (error) {
      // Nothing was stored, so a cancelled save needs no rollback and no
      // error toast for an action the user abandoned.
      if (disposed || epoch !== saveEpoch) return
      console.error(
        '[ImpowerX] Verwaltungsgebühr-Einstellungen konnten nicht gespeichert werden:',
        error
      )
      showToast(
        'Die Verwaltungsgebühr-Einstellungen konnten nicht gespeichert werden.',
        'error'
      )
    } finally {
      isSaving = false
      if (saveQueued) {
        saveQueued = false
        flushSave()
      }
    }
  }

  const restoreSavedState = () => {
    if (!savedSettings) return
    saveEpoch += 1
    state = { ...savedSettings }
    syncUi()
  }

  const buttonDisconnects = []
  SAVE_BUTTON_LABELS.forEach((label) => {
    buttonDisconnects.push(listenToButtonClick(label, flushSave))
  })
  CANCEL_BUTTON_LABELS.forEach((label) => {
    buttonDisconnects.push(listenToButtonClick(label, restoreSavedState))
  })
  const onDocumentClick = (event) => {
    if (event.target.closest('.lux-fee-pct-select, .lux-fee-pct-dropdown-wrap')) {
      return
    }
    document
      .querySelectorAll(`.${DETAILS_CLASS}`)
      .forEach((details) => closeRentBaseDropdown(details))
  }
  document.addEventListener('click', onDocumentClick)
  buttonDisconnects.push(() =>
    document.removeEventListener('click', onDocumentClick)
  )

  // Re-inject/refresh after Impower re-renders (section open, edit-mode toggle,
  // save). Debounced; syncUi only writes on diffs so the observer settles.
  const observer = new MutationObserver(() => {
    if (disposed) return
    clearTimeout(syncTimer)
    syncTimer = setTimeout(() => {
      ensureInjected()
      syncUi()
    }, 150)
  })
  observer.observe(document.body, { childList: true, subtree: true })

  loadSettings()

  return () => {
    disposed = true
    clearTimeout(syncTimer)
    observer.disconnect()
    buttonDisconnects.forEach((disconnect) => {
      if (typeof disconnect === 'function') disconnect()
    })
    document
      .querySelectorAll(`.${RADIO_CLASS}, .${DETAILS_CLASS}`)
      .forEach((el) => el.remove())
    document.getElementById(STYLE_ID)?.remove()
  }
}
